/**
 * Скільки записів читати за раз при перебудові.
 *
 * Щоденник за десять років — це тисячі записів із CRDT-документами, і
 * прочитати їх усі в памʼять означало б покласти вкладку рівно тоді, коли
 * користувач уперше вирішив щось знайти.
 */
export const REBUILD_BATCH = 200;
export class SqlSearch {
    db;
    extractText;
    constructor(db, extractText) {
        this.db = db;
        this.extractText = extractText;
    }
    /** Номер рядка індексу для запису; створюється при першій індексації. */
    async #docId(entryId) {
        const found = await this.db.query("SELECT docid FROM entry_fts_doc WHERE entry_id = ?", [entryId]);
        if (found[0])
            return found[0].docid;
        await this.db.exec("INSERT INTO entry_fts_doc (entry_id) VALUES (?)", [entryId]);
        const created = await this.db.query("SELECT docid FROM entry_fts_doc WHERE entry_id = ?", [entryId]);
        const docid = created[0]?.docid;
        if (docid === undefined)
            throw new Error(`не вдалося завести рядок індексу для ${entryId}`);
        return docid;
    }
    async index(entryId, title, body) {
        // Розбір тіла тут, а не в того, хто кличе: ядро не має знати про CRDT.
        const text = await this.extractText(body);
        const docid = await this.#docId(entryId);
        await this.db.transaction(async (tx) => {
            // Спершу прибрати старе: без цього відредагований запис лишався б
            // у пошуку і за старими словами теж. Саме заради цього рядка
            // існує міграція 002.
            await tx.exec("DELETE FROM entry_fts WHERE rowid = ?", [docid]);
            await tx.exec("INSERT INTO entry_fts (rowid, title, body) VALUES (?, ?, ?)", [
                docid,
                title,
                text,
            ]);
        });
    }
    async remove(entryId) {
        const found = await this.db.query("SELECT docid FROM entry_fts_doc WHERE entry_id = ?", [entryId]);
        const docid = found[0]?.docid;
        if (docid === undefined)
            return;
        await this.db.transaction(async (tx) => {
            await tx.exec("DELETE FROM entry_fts WHERE rowid = ?", [docid]);
            await tx.exec("DELETE FROM entry_fts_doc WHERE entry_id = ?", [entryId]);
        });
    }
    async query(text, limit = 50) {
        const match = toMatchQuery(text);
        if (match === null)
            return [];
        const rows = await this.db.query(`SELECT d.entry_id AS entry_id, f.rank AS rank
         FROM entry_fts f
         JOIN entry_fts_doc d ON d.docid = f.rowid
         JOIN entry e ON e.id = d.entry_id
        WHERE entry_fts MATCH ?
          AND e.deleted_at IS NULL
        ORDER BY f.rank
        LIMIT ?`, [match, limit]);
        return rows.map((r) => ({ entryId: r.entry_id, rank: r.rank }));
    }
    /**
     * Перебудова індексу із записів. [ADR-0014]
     *
     * Безконтентний індекс не вміє `INSERT INTO t(t) VALUES('rebuild')`:
     * перечитувати нема чого. Тому джерелом виступає таблиця `entry`, а
     * розкодування CRDT робить `extractText`.
     */
    async rebuild() {
        await this.db.exec("DELETE FROM entry_fts");
        await this.db.exec("DELETE FROM entry_fts_doc");
        let after = "";
        for (;;) {
            // Порціями за id: UUIDv7 монотонний, тож це стабільний обхід без
            // OFFSET — який на великій таблиці щоразу перечитує початок.
            const batch = await this.db.query(`SELECT id, title, body FROM entry
          WHERE deleted_at IS NULL AND id > ?
          ORDER BY id
          LIMIT ?`, [after, REBUILD_BATCH]);
            if (batch.length === 0)
                return;
            for (const row of batch) {
                const body = row.body instanceof Uint8Array ? row.body : null;
                await this.index(row.id, typeof row.title === "string" ? row.title : "", body);
            }
            after = batch[batch.length - 1]?.id ?? "";
        }
    }
}
/**
 * Перетворює те, що набрав користувач, на запит FTS5.
 *
 * Синтаксис MATCH має власні особливі символи (`"`, `*`, `-`, `NEAR`, `OR`),
 * і рядок із них падає з помилкою розбору. Користувач шукає слова, а не пише
 * запити, тож слова беруться в лапки, а решта відкидається.
 *
 * Повертає `null`, якщо шукати нема чого — інакше порожній MATCH віддав би
 * геть усе.
 */
export function toMatchQuery(text) {
    const words = text
        .split(/\s+/)
        .map((w) => w.replace(/"/g, "").trim())
        .filter((w) => w.length > 0);
    if (words.length === 0)
        return null;
    // Останнє слово з зірочкою: користувач часто ще не дописав його.
    return words.map((w, i) => (i === words.length - 1 ? `"${w}"*` : `"${w}"`)).join(" ");
}
//# sourceMappingURL=search.js.map